import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { api } from "@/lib/api";
import { Reveal } from "@/components/Reveal";
import NewsletterForm from "@/components/NewsletterForm";

const CONFIDENCE_STYLE = {
  HIGH: "text-emerald-400 border-emerald-400/30",
  MODERATE: "text-amber-400 border-amber-400/30",
  EXPLORATORY: "text-hussar-slate border-white/15",
};

export default function Intelligence() {
  const [articles, setArticles] = useState(null);
  const [filter, setFilter] = useState("ALL");

  useEffect(() => {
    document.title = "Weekly Intelligence Archive | Hussar Treasury";
    let link = document.head.querySelector('link[rel="canonical"]');
    if (!link) { link = document.createElement("link"); link.setAttribute("rel", "canonical"); document.head.appendChild(link); }
    link.setAttribute("href", window.location.origin + "/intelligence");
    api.get("/articles").then((r) => setArticles(r.data)).catch(() => setArticles([]));
  }, []);

  const categories = ["ALL", ...new Set((articles || []).map((a) => a.category))];
  const visible = (articles || []).filter((a) => filter === "ALL" || a.category === filter);

  return (
    <div data-testid="intelligence-page" className="pt-40 px-6 sm:px-10 lg:px-16 max-w-[1600px] mx-auto pb-28">
      <Reveal>
        <div className="metadata-label mb-6">The Permanent Public Record</div>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-7xl text-hussar-ink tracking-tight leading-[1.05] max-w-4xl">
          Weekly Intelligence
        </h1>
        <p className="mt-8 max-w-2xl text-hussar-slate text-base sm:text-lg font-light leading-relaxed">
          Sanitized, owner-approved extracts of Hussar Lab research. Evidence-dated, confidence-labelled,
          versioned, and never silently rewritten.
        </p>
      </Reveal>

      <div className="gold-hairline my-16" />

      {articles === null ? (
        <div className="py-20 text-center text-hussar-muted font-mono text-xs uppercase tracking-widest">Loading the record…</div>
      ) : articles.length === 0 ? (
        <Reveal>
          <div data-testid="archive-empty-state" className="border border-hussar-gold/20 bg-hussar-card p-10 sm:p-16 relative overflow-hidden max-w-4xl">
            <div className="absolute top-0 left-0 w-1 h-full bg-hussar-gold/60" />
            <div className="metadata-label mb-4">Edition No. 001 — In the Pipeline</div>
            <h2 className="font-serif text-2xl sm:text-3xl text-hussar-ink leading-snug mb-6">
              The first edition is moving through sanitization and owner approval.
            </h2>
            <p className="text-hussar-slate font-light leading-relaxed mb-10 max-w-2xl">
              Weekly Intelligence is not published on a content calendar — it is published when the private Hussar Lab
              Learning Report has been sanitized and explicitly approved. Subscribe to receive the first edition the
              moment it enters the public record.
            </p>
            <NewsletterForm source="archive" />
          </div>
        </Reveal>
      ) : (
        <>
          <div className="flex flex-wrap gap-3 mb-14" data-testid="category-filters">
            {categories.map((c) => (
              <button
                key={c}
                onClick={() => setFilter(c)}
                data-testid={`filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
                className={`font-mono text-[11px] uppercase tracking-[0.2em] px-5 py-2.5 border transition-colors duration-300 ${
                  filter === c ? "border-hussar-gold text-hussar-gold bg-hussar-gold/5" : "border-white/10 text-hussar-muted hover:border-hussar-gold/40 hover:text-hussar-slate"
                }`}
              >
                {c}
              </button>
            ))}
          </div>

          <div className="flex flex-col" data-testid="article-list">
            {visible.map((a, i) => (
              <Reveal key={a.slug} delay={Math.min(i * 0.06, 0.3)}>
                <Link
                  to={`/intelligence/${a.slug}`}
                  data-testid={`article-row-${a.slug}`}
                  className="group grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-8 py-10 border-t border-white/5 hover:border-hussar-gold/30 transition-colors duration-500"
                >
                  <div className="lg:col-span-2 font-mono text-[11px] uppercase tracking-[0.2em] text-hussar-muted space-y-2">
                    <div>{a.published_at ? new Date(a.published_at).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—"}</div>
                    <div className="text-hussar-gold/60">v{a.version}</div>
                    {a.status === "UNPUBLISHED" && <div className="text-red-400/80">Withdrawn</div>}
                  </div>
                  <div className="lg:col-span-7">
                    <h2 className="font-serif text-2xl sm:text-3xl text-hussar-ink group-hover:text-hussar-goldbright transition-colors duration-300 leading-snug">
                      {a.title}
                    </h2>
                    <p className="text-hussar-slate text-sm font-light leading-relaxed mt-3 max-w-2xl">{a.dek}</p>
                  </div>
                  <div className="lg:col-span-3 flex lg:flex-col items-start lg:items-end justify-between gap-3">
                    <span className={`font-mono text-[10px] uppercase tracking-[0.2em] border px-3 py-1.5 ${CONFIDENCE_STYLE[a.confidence_label] || CONFIDENCE_STYLE.EXPLORATORY}`}>
                      {a.confidence_label}
                    </span>
                    <span className="text-hussar-gold opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <ArrowUpRight size={18} />
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
